// ============================================================================
// Subcontractor directory + profile data hooks. Flat-fetch over the
// v_subcontractor_* views shipped in migration 20260611 (read-only,
// security_invoker — RLS scopes everything to the signed-in user).
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import type { CoiState, W9State } from '../../../shared/coi-state';

// ----- Row shapes (local — views aren't in generated types until next regen)

export interface SubScorecardRow {
  provider_id: string;
  partner_name: string;
  market_region: string | null;
  provider_status: string | null;
  // NAMED STATES from v_provider_compliance_status (via the scorecard view),
  // not the retired 'Yes'/'No' mirror. ⛔ Never read either as a boolean —
  // use w9OnFile() / coiCovered() from shared/coi-state.
  w9_status: W9State | string | null;
  coi_status: CoiState | string | null;
  active_facilities: number;
  monthly_client_billing: number;
  monthly_sub_cost: number;
  monthly_gross_margin: number;
  margin_pct: number | null;
  feedback_total: number;
  negative_fb: number;
  open_fb: number;
  fb_precise: number;
  fb_baseline: number;
  fb_t90: number;
  negative_fb_t90: number;
  // Billing/cost/margin above cover FLAT-billed work only; hourly/variable
  // facilities (no facilities.monthly_rate) are reported separately so they
  // can't fake negative margins (the EM Services lesson).
  flat_facilities: number;
  variable_facilities: number;
  variable_sub_cost: number;
}

export interface SubFacilityRow {
  provider_id: string;
  partner_name: string;
  facility_id: string;
  facility_name: string;
  market_region: string | null;
  assignment_id: string;
  service_code: string | null;
  service_label: string | null;
  sub_rate_monthly: number | null;
  client_billing_monthly: number | null;
  gross_margin_monthly: number | null;
  account_manager_eid: string | null;
  billing_method: string | null;
}

export interface SubFeedbackRow {
  feedback_id: string;
  provider_id: string;
  facility_id: string;
  feedback_date: string;
  sentiment: string | null;
  status: string | null;
  category: string | null;
  attribution_quality: 'precise' | 'baseline';
}

export interface SubAliasRow {
  entity_id: string;
  alias: string;
  alias_normalized: string;
}

export interface SubProviderDetail {
  provider_id: string;
  partner_name: string;
  owner_name: string | null;
  legal_business_name: string | null;
  /** The markets this sub covers — real markets only (item #683). */
  coverage_markets: string[] | null;
  provider_status: string | null;
  email: string | null;
  phone: string | null;
  primary_poc_name: string | null;
  primary_poc_phone: string | null;
  primary_poc_email: string | null;
  secondary_poc_name: string | null;
  secondary_poc_phone: string | null;
  secondary_poc_email: string | null;
}

// ----- Directory: all scorecards + alias map (97 subs — load once, filter
// client-side, same approach SmartSelect takes).

// 2026-07-06 — provider name fields fetched once alongside the scorecard so
// the directory search matches owner / legal / POC names directly (not only
// when someone has minted an alias for them).
export interface SubNameFields {
  provider_id: string;
  owner_name: string | null;
  legal_business_name: string | null;
  primary_poc_name: string | null;
  secondary_poc_name: string | null;
}

export function useSubDirectory() {
  const [rows, setRows] = useState<SubScorecardRow[]>([]);
  const [aliases, setAliases] = useState<SubAliasRow[]>([]);
  const [nameFields, setNameFields] = useState<Map<string, SubNameFields>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          { data: sc, error: e1 },
          { data: al, error: e2 },
          { data: nf, error: e3 },
        ] = await Promise.all([
          supabase
            .from('v_subcontractor_scorecard')
            .select('*')
            .order('active_facilities', { ascending: false }),
          supabase
            .from('entity_aliases')
            .select('entity_id, alias, alias_normalized')
            .eq('entity_type', 'service_provider'),
          supabase
            .from('service_providers')
            .select(
              'provider_id, owner_name, legal_business_name, primary_poc_name, secondary_poc_name',
            ),
        ]);
        if (e1) throw e1;
        if (e2) throw e2;
        if (e3) throw e3;
        if (cancelled) return;
        setRows((sc ?? []) as SubScorecardRow[]);
        setAliases((al ?? []) as SubAliasRow[]);
        const map = new Map<string, SubNameFields>();
        for (const r of (nf ?? []) as SubNameFields[]) {
          map.set(r.provider_id, r);
        }
        setNameFields(map);
      } catch (e) {
        if (!cancelled) setError(rpcErrorMessage(e, 'Could not load subcontractors.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { rows, aliases, nameFields, loading, error, refetch };
}

// ----- Profile: one sub — provider row + scorecard + facilities + feedback.

export function useSubDetail(providerId: string | undefined) {
  const [provider, setProvider] = useState<SubProviderDetail | null>(null);
  const [scorecard, setScorecard] = useState<SubScorecardRow | null>(null);
  const [facilities, setFacilities] = useState<SubFacilityRow[]>([]);
  const [feedback, setFeedback] = useState<SubFeedbackRow[]>([]);
  const [facilityNames, setFacilityNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!providerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [provRes, scRes, facRes, fbRes] = await Promise.all([
          supabase
            .from('service_providers')
            .select(
              'provider_id, partner_name, owner_name, legal_business_name, coverage_markets, provider_status, email, phone, primary_poc_name, primary_poc_phone, primary_poc_email, secondary_poc_name, secondary_poc_phone, secondary_poc_email',
            )
            .eq('provider_id', providerId)
            .maybeSingle(),
          supabase
            .from('v_subcontractor_scorecard')
            .select('*')
            .eq('provider_id', providerId)
            .maybeSingle(),
          supabase
            .from('v_subcontractor_facilities')
            .select('*')
            .eq('provider_id', providerId)
            .order('facility_name'),
          supabase
            .from('v_subcontractor_feedback')
            .select('*')
            .eq('provider_id', providerId)
            .order('feedback_date', { ascending: false })
            .limit(15),
        ]);
        if (provRes.error) throw provRes.error;
        if (scRes.error) throw scRes.error;
        if (facRes.error) throw facRes.error;
        if (fbRes.error) throw fbRes.error;
        if (cancelled) return;

        const fb = (fbRes.data ?? []) as SubFeedbackRow[];
        setProvider((provRes.data ?? null) as SubProviderDetail | null);
        setScorecard((scRes.data ?? null) as SubScorecardRow | null);
        setFacilities((facRes.data ?? []) as SubFacilityRow[]);
        setFeedback(fb);

        // Facility names for feedback rows beyond the active list (ended
        // assignments / inactive facilities) — flat-fetch + client map.
        const map: Record<string, string> = {};
        for (const f of facRes.data ?? []) {
          map[f.facility_id as string] = f.facility_name as string;
        }
        const missing = [...new Set(fb.map((r) => r.facility_id))].filter(
          (id) => !(id in map),
        );
        if (missing.length > 0) {
          const { data: extra } = await supabase
            .from('facilities')
            .select('facility_id, facility_name')
            .in('facility_id', missing);
          if (cancelled) return;
          for (const f of extra ?? []) {
            map[f.facility_id as string] = f.facility_name as string;
          }
        }
        setFacilityNames(map);
      } catch (e) {
        if (!cancelled) setError(rpcErrorMessage(e, 'Could not load this subcontractor.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [providerId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { provider, scorecard, facilities, feedback, facilityNames, loading, error, refetch };
}

// ----- Mutations

// Patch a service_providers row (HR edit of a subcontractor card). HR / superuser
// have UPDATE via RLS (hr_update / superuser_all); other roles get an RLS error
// the caller surfaces.
export async function updateProvider(
  providerId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('service_providers')
    .update(patch)
    .eq('provider_id', providerId);
  if (error) throw error;
}

// ----- Shared formatting helpers

export const fmtMoney = (n: number | null | undefined) =>
  n == null
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export const fmtPct = (n: number | null | undefined) =>
  n == null ? '—' : `${Number(n).toFixed(1)}%`;
